import { getCurrentInstance, effectScope, shallowReactive, useCssModule, computed } from "vue";
import { u as useI18n } from "./assets/index-CgZzOVq6.js";
import { isVue2 } from "vue-demi";
import { u as useDataTestIdProp } from "./assets/index-BNd8MWnO.js";
import "./assets/index-vzP2cezV.js";
import * as vueRouter from "vue-router";
import "dompurify";
import "@vueuse/core";
import XLink from "./XLink.js";
import { n as normalizeComponent } from "./assets/_plugin-vue2_normalizer-VvYS6rEj.js";
import "vue-i18n";
import "./locales.js";
import "./assets/index-DKxr0Yto.js";
import "./assets/XIcon-U9bwokTN.js";
import "@fortawesome/vue-fontawesome";
import "./assets/BaseLink-KuovKwoP.js";
import "./assets/index-Ca8wHgNc.js";
const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);
const v2UseRoute = () => {
  const root = getCurrentInstance().proxy.$root;
  if (!root.$router) return void 0;
  if (!root._$route) {
    const route = effectScope(true).run(() => shallowReactive({ ...root.$router.currentRoute }));
    root._$route = route;
    root.$router.afterEach((to) => {
      Object.assign(route, to);
    });
  }
  return root._$route;
};
const useRoute = isVue2 ? v2UseRoute : vueRouter.useRoute;
function useAlignment(props) {
  const $style = useCssModule();
  const alignClass = computed(() => {
    const className = `align${capitalize(props.align)}`;
    return {
      [$style[className]]: true
    };
  });
  return {
    alignClass
  };
}
function usePagesNavigation(props, emit) {
  const route = useRoute();
  const currentPage = computed(() => {
    var _a;
    if (props.eventBased) return props.activePage;
    const page = ((_a = route == null ? void 0 : route.query) == null ? void 0 : _a.page) || new URLSearchParams(window.location.search).get("page");
    const pageParameter = Array.isArray(page) ? page[0] : page;
    return Number(pageParameter) || 1;
  });
  function isCurrentPage(page) {
    return currentPage.value === page;
  }
  function getPageLink(page) {
    if (props.eventBased) return "";
    const url = new URL(window.location.href);
    url.searchParams.set("page", page);
    return url.toString();
  }
  function changePage(event, page) {
    if (isCurrentPage(page) || props.eventBased) event.preventDefault();
    if (props.eventBased) emit("change", page);
  }
  const hasPrevPage = computed(() => currentPage.value - 1 <= 0);
  const hasNextPage = computed(() => currentPage.value + 1 > props.totalPages);
  return {
    currentPage,
    hasPrevPage,
    hasNextPage,
    getPageLink,
    isCurrentPage,
    changePage
  };
}
function usePagesVisibility(props, currentPage) {
  const numberOfVisiblePages = computed(
    () => props.totalPages > props.maxVisible ? props.maxVisible : props.totalPages
  );
  const havePagesToHide = computed(() => props.totalPages > props.maxVisible);
  const pageLimiterTriggerPoint = computed(
    () => props.totalPages > numberOfVisiblePages.value ? Math.ceil(numberOfVisiblePages.value / 2) : props.totalPages
  );
  function getVisiblePages() {
    return Array.from(Array(numberOfVisiblePages.value), (_, index) => {
      const startIndex = currentPage.value > pageLimiterTriggerPoint.value ? currentPage.value - 2 : 1;
      const page = havePagesToHide.value ? startIndex + index : currentPage.value + index;
      return page > props.totalPages ? page - numberOfVisiblePages.value : page;
    });
  }
  function sortVisiblePages(visiblePagesToSort) {
    return visiblePagesToSort.sort((a, b) => a - b).map((page, index) => {
      if (!havePagesToHide.value) return page;
      if (index === 0) return 1;
      return index + 1 === props.maxVisible ? props.totalPages : page;
    });
  }
  const visiblePages = computed(() => {
    return sortVisiblePages(getVisiblePages());
  });
  function showLimiter(pageIndex) {
    const visiblePage = visiblePages.value[pageIndex];
    const prevVisiblePage = visiblePages.value[pageIndex - 1];
    const isLastVisiblePage = pageIndex === props.maxVisible - 1;
    const isSecondVisiblePage = pageIndex === 1;
    const isAfterTriggerPoint = currentPage.value - 1 > 2 && currentPage.value - 1 >= pageLimiterTriggerPoint.value;
    const showStartLimiter = isSecondVisiblePage && isAfterTriggerPoint;
    const showEndLimiter = isLastVisiblePage && visiblePage - prevVisiblePage >= 2;
    return havePagesToHide.value && (showEndLimiter || showStartLimiter);
  }
  return {
    pageLimiterTriggerPoint,
    havePagesToHide,
    visiblePages,
    showLimiter
  };
}
const xPagination = "_xPagination_12e40_1";
const alignLeft = "_alignLeft_12e40_6";
const alignRight = "_alignRight_12e40_9";
const alignCenter = "_alignCenter_12e40_12";
const xPaginationContainer = "_xPaginationContainer_12e40_16";
const xPaginationItem = "_xPaginationItem_12e40_25";
const xPaginationLink = "_xPaginationLink_12e40_32";
const xPaginationNav = "_xPaginationNav_12e40_33";
const xPaginationLimiter = "_xPaginationLimiter_12e40_38";
const xPaginationNavText = "_xPaginationNavText_12e40_53";
const style0 = {
  xPagination,
  alignLeft,
  alignRight,
  alignCenter,
  xPaginationContainer,
  xPaginationItem,
  xPaginationLink,
  xPaginationNav,
  xPaginationLimiter,
  xPaginationNavText
};
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const __default__ = {
  name: "XPagination",
  components: {
    XLink
  },
  model: {
    prop: "activePage",
    event: "change"
  }
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    activePage: {
      type: Number,
      default: 1
    },
    totalPages: {
      type: Number,
      default: 2
    },
    maxVisible: {
      type: Number,
      default: 7,
      validator: (maxVisiblePages) => maxVisiblePages >= 3
    },
    prevText: {
      type: String,
      default: ""
    },
    nextText: {
      type: String,
      default: ""
    },
    eventBased: {
      type: Boolean,
      default: false
    },
    align: {
      type: String,
      default: "left",
      validator: (position) => /left|center|right/.test(position)
    },
    ...createTestIdProp()
  },
  emits: ["change"],
  setup(__props, { emit }) {
    const props = __props;
    const { t } = useI18n();
    const { formatTestId } = setupTestId(props);
    const { alignClass } = useAlignment(props);
    const { currentPage, hasNextPage, hasPrevPage, getPageLink, isCurrentPage, changePage } = usePagesNavigation(props, emit);
    const { visiblePages, showLimiter } = usePagesVisibility(props, currentPage);
    return { __sfc: true, createTestIdProp, setupTestId, props, emit, t, formatTestId, alignClass, currentPage, hasNextPage, hasPrevPage, getPageLink, isCurrentPage, changePage, visiblePages, showLimiter, XLink };
  }
});
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c, _setup = _vm._self._setupProxy;
  return _c("nav", { class: [_vm.$style.xPagination, _setup.alignClass], attrs: { "data-testid": _setup.formatTestId(), "role": "navigation", "aria-label": _setup.t("ds.xPagination.aria.label") } }, [_c("ul", { class: _vm.$style.xPaginationContainer }, [_c("li", { class: _vm.$style.xPaginationItem }, [_c(_setup.XLink, { class: _vm.$style.xPaginationNav, attrs: { "to": _setup.getPageLink(_setup.currentPage - 1), "disabled": _setup.hasPrevPage, "data-testid": _setup.formatTestId("previousLink"), "icon": "arrow-left", "icon-position": "left", "no-padding": "", "title": _setup.t("ds.xPagination.prev") }, on: { "click": function($event) {
    return _setup.changePage($event, _setup.currentPage - 1);
  } } }, [_vm.prevText ? _c("div", { class: _vm.$style.xPaginationNavText, attrs: { "data-testid": _setup.formatTestId("previousText") } }, [_vm._v(" " + _vm._s(_vm.prevText) + " ")]) : _vm._e()])], 1), _vm._l(_setup.visiblePages, function(page, index) {
    return _c("li", { key: page, class: _vm.$style.xPaginationItem }, [_c("span", { directives: [{ name: "show", rawName: "v-show", value: _setup.showLimiter(index), expression: "showLimiter(index)" }], class: _vm.$style.xPaginationLimiter }, [_vm._v(" ... ")]), _c(_setup.XLink, { class: _vm.$style.xPaginationLink, attrs: { "to": _setup.getPageLink(page), "disabled": _setup.isCurrentPage(page), "aria-current": _setup.isCurrentPage(page), "data-testid": _setup.formatTestId(`page${page}Link`), "no-padding": "" }, on: { "click": function($event) {
      return _setup.changePage($event, page);
    } } }, [_vm._v(" " + _vm._s(page) + " ")])], 1);
  }), _c("li", { class: _vm.$style.xPaginationItem }, [_c(_setup.XLink, { class: _vm.$style.xPaginationNav, attrs: { "to": _setup.getPageLink(_setup.currentPage + 1), "disabled": _setup.hasNextPage, "data-testid": _setup.formatTestId("nextLink"), "icon": "arrow-right", "icon-position": "right", "no-padding": "", "title": _setup.t("ds.xPagination.next") }, on: { "click": function($event) {
    return _setup.changePage($event, _setup.currentPage + 1);
  } } }, [_vm.nextText ? _c("div", { class: _vm.$style.xPaginationNavText, attrs: { "data-testid": _setup.formatTestId("nextText") } }, [_vm._v(" " + _vm._s(_vm.nextText) + " ")]) : _vm._e()])], 1)], 2)]);
};
var _sfc_staticRenderFns = [];
const __cssModules = {
  "$style": style0
};
function _sfc_injectStyles(ctx) {
  for (var key in __cssModules) {
    this[key] = __cssModules[key];
  }
}
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  _sfc_injectStyles
);
const XPagination = __component__.exports;
export {
  XPagination as default
};
